<template>
  <div class="babylon-container">
    <canvas ref="renderCanvas" id="renderCanvas"></canvas>
    <!-- <CreateDecal v-if="currentScene" :scene="currentScene" /> -->
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted, ref, computed } from 'vue'
import { BabylonScene } from '../Scene/Scene'
// import CreateDecal from './CreateDecal.vue'

export default defineComponent({
  name: 'SceneViewer',
  components: {
    // CreateDecal
  },
  props: {},
  emits: ['loading-changed'],
  setup(props, { emit }) {
    const renderCanvas = ref<HTMLCanvasElement | null>(null)
    const babylonScene = ref<BabylonScene | null>(null)
    // Serialize scene mutations to avoid async race conditions (knob type -> material).
    let sceneOpQueue: Promise<void> = Promise.resolve()

    const enqueueSceneOp = (op: () => Promise<void> | void) => {
      sceneOpQueue = sceneOpQueue
        .then(async () => {
          await op()
        })
        .catch((e) => {
          console.error('SceneViewer: scene op failed:', e)
        })
    }

    const currentScene = computed(() => babylonScene.value?.getScene())

    // Prevent wheel/trackpad scrolling from bubbling to the parent page when
    // the pointer is over the 3D scene (common in Shopify embeds).
    const preventPageScrollOnCanvasWheel = (e: WheelEvent) => {
      e.preventDefault()
      e.stopPropagation()
    }

    const initializeBabylonScene = () => {
      if (renderCanvas.value && !babylonScene.value) {
        console.log('Initializing Babylon scene after full page load...')
        // Immediately show loading overlay before any rendering begins.
        emit('loading-changed', true)
        babylonScene.value = new BabylonScene(renderCanvas.value)
        
        // Set up loading callback to emit events to parent
        babylonScene.value.setLoadingCallback((loading: boolean) => {
          console.log('SceneViewer: Emitting loading state to parent:', loading)
          emit('loading-changed', loading)
        })
        
        babylonScene.value.startRenderLoop()
      }
    }

    let loadHandler: (() => void) | null = null
    let fallbackTimeout: number | null = null

    onMounted(() => {
      console.log('SceneViewer mounted, document.readyState:', document.readyState)

      // Stop scroll chaining from the scene area to the page.
      if (renderCanvas.value) {
        renderCanvas.value.addEventListener('wheel', preventPageScrollOnCanvasWheel, { passive: false })
      }
      
      // Check if window is already loaded (in case onMounted fires after load)
      if (document.readyState === 'complete') {
        console.log('Document already complete, initializing with timeout...')
        // Use a small timeout to ensure viewport has settled on iOS
        setTimeout(() => {
          initializeBabylonScene()
        }, 100)
      } else {
        console.log('Document not complete, waiting for window load...')
        
        // Create the load handler
        loadHandler = () => {
          console.log('Window load event fired, initializing scene...')
          if (fallbackTimeout) {
            clearTimeout(fallbackTimeout)
            fallbackTimeout = null
          }
          // Additional timeout for iOS Safari viewport settling
          setTimeout(() => {
            initializeBabylonScene()
          }, 100)
        }
        
        // Wait for complete page load including images, fonts, and layout
        window.addEventListener('load', loadHandler)
        
        // Fallback timeout in case load event doesn't fire (shouldn't happen, but safety net)
        fallbackTimeout = window.setTimeout(() => {
          console.log('Fallback timeout triggered, initializing scene...')
          if (loadHandler) {
            window.removeEventListener('load', loadHandler)
            loadHandler = null
          }
          initializeBabylonScene()
        }, 5000) // 5 second fallback
      }
    })

    onUnmounted(() => {
      if (renderCanvas.value) {
        renderCanvas.value.removeEventListener('wheel', preventPageScrollOnCanvasWheel)
      }

      // Clean up event listener if it exists
      if (loadHandler) {
        window.removeEventListener('load', loadHandler)
        loadHandler = null
      }
      
      // Clean up fallback timeout
      if (fallbackTimeout) {
        clearTimeout(fallbackTimeout)
        fallbackTimeout = null
      }
      
      if (babylonScene.value) {
        babylonScene.value.stopRenderLoop()
        babylonScene.value.dispose()
        babylonScene.value = null
      }
    })

    const switchKnobType = (knobTypeId: string) => {
      console.log('SceneViewer: Switching knob type to:', knobTypeId);
      if (babylonScene.value) {
        enqueueSceneOp(() => babylonScene.value!.switchKnobType(knobTypeId))
      }
    }

    const switchMaterial = (materialId: string) => {
      console.log('SceneViewer: Switching material to:', materialId);
      if (babylonScene.value) {
        enqueueSceneOp(() => babylonScene.value!.switchMaterial(materialId))
      }
    }

    const switchEnclosureMaterial = (materialId: string) => {
      console.log('SceneViewer: Switching enclosure material to:', materialId);
      if (babylonScene.value) {
        enqueueSceneOp(() => babylonScene.value!.switchEnclosureMaterial(materialId))
      }
    }

    const switchLEDMaterial = (materialId: string) => {
      console.log('SceneViewer: Switching LED material to:', materialId);
      if (babylonScene.value) {
        enqueueSceneOp(() => { babylonScene.value!.switchLEDMaterial(materialId) })
      }
    }

    return {
      renderCanvas,
      currentScene,
      switchKnobType,
      switchMaterial,
      switchEnclosureMaterial,
      switchLEDMaterial
    }
  }
})
</script>

<style>
.babylon-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  /* Prevent scroll chaining to the parent page */
  overscroll-behavior: contain;
}

#renderCanvas {
  width: 100%;
  height: 100%;
  touch-action: none;
  outline: none;
  /* Prevent scroll chaining to the parent page */
  overscroll-behavior: contain;
}
</style> 